import { AfterViewInit, Component, EventEmitter, ViewChild, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { NotificationService } from '../../services/notification.service';
import { ConfirmationService } from 'primeng/api';
import { InputTextarea } from 'primeng/inputtextarea';
import { AlertType } from '@common/enums/AlertType';
import { InvestmentActivity } from '@common/enums/InvestmentActivity';
import { ICalendarItem } from '@common/models/ICalendarItem';
import { Team, TeamLabel } from '@common/enums/Team';
import { ReminderFrequency } from '@common/enums/ReminderFrequency';
import { QuangularToasterService } from '@app/services/common/toaster.service';
import { PortfolioReviewService } from '@app/modules/portfolio/services/portfolio-review.service';
import { CompanyStage } from '@common/enums/CompanyStage';
import { QuarterService } from '../../services/quarter.service';
import { IAlertEvent } from '@common/models/IAlertEvent';
import { Utils } from '@common/utils/utils';
import { TimeZoneService } from '@app/services/common/timezone.service';

@Component({
  selector: 'app-add-new-events',
  templateUrl: './add-new-events.component.html',
  styleUrls: ['./add-new-events.component.scss']
})
export class AddNewEventsComponent implements OnChanges, AfterViewInit, OnInit, OnDestroy {
  createEventForm: FormGroup;
  private subscription: Subscription = new Subscription();
  minDate = new Date(Date.now());
  @Input() selectedEvent;
  @Input() readonly: boolean = false;
  @ViewChild(InputTextarea) descriptionInput?: InputTextarea;
  @Input() isPublishEvent: boolean = false;
  @Input() header: string = 'Edit Event';
  @Input() isAddTask: boolean = false;
  @Input() portfolioCompanyOptions: { label: string; value: string }[] = [];
  @Input() set visible(val: any) { this._visible = !!val; }
  @Output() visibleChange = new EventEmitter<any>();
  @Output() hide = new EventEmitter();
  @Output() isSubmitted = new EventEmitter();
  @Output() newEvent = new EventEmitter();

  _visible = false;
  private _programmaticClose = false;
  showExcludedDialog = false;
  quartersOfYear: any[] = [];

  minDateFromDb: Date;
  investmentTypes = this.notificationService.getInvestmentTypes();
  associationOptions = [
    { label: InvestmentActivity.Valuation, value: InvestmentActivity.Valuation },
    { label: InvestmentActivity.Documents, value: InvestmentActivity.Documents },
    { label: InvestmentActivity.Tearsheets, value: InvestmentActivity.Tearsheets },
    { label: InvestmentActivity.Review, value: InvestmentActivity.Review }
  ];
  readonly EVENT_DESC_PLACEHOLDER = "Send data requests and engineering letters to portfolio companies";
  alertTypeOptions = [
    { label: AlertType.In_App_Notification, value: AlertType.In_App_Notification },
    { label: AlertType.Email, value: AlertType.Email },
    { label: AlertType.Both, value: AlertType.Both },
    { label: AlertType.None, value: AlertType.None }
  ];

  teamOptions = [
    { value: Team.Accounting, label: TeamLabel.finance },
    { value: Team.Deal_Execution, label: TeamLabel.Deal_Execution },
    { value: Team.Investor_Relations, label: TeamLabel.investorRelations },
    { value: Team.Technical, label: TeamLabel.Technical },
    { value: Team.Treasury, label: TeamLabel.Treasury },
    { value: Team.Deal_Senior, label: TeamLabel.Deal_Senior }
  ];

  constructor(
    private readonly notificationService: NotificationService,
    public readonly confirmationService: ConfirmationService,
    private readonly qToaster: QuangularToasterService,
    private readonly portfolioReviewService: PortfolioReviewService,
    private readonly quarterService: QuarterService,
    private readonly timeZoneService: TimeZoneService, 
  ) {

    this.minDate.setDate(this.minDate.getDate() + 1);

    this.createEventForm = new FormGroup({
      title: new FormControl('', Validators.required),
      eventType: new FormControl(null, Validators.required),
      startDate: new FormControl(null, Validators.required),
      endDate: new FormControl(null, Validators.required),
      description: new FormControl(''),
      teamAssignment: new FormControl([]),
      createdBy: new FormControl(''),
      isPublished: new FormControl(false),
      quarter: new FormControl(''),
      isAdmin: new FormControl(false),
      lastUpdatedBy: new FormControl(''),
      portfolioCompanies: new FormControl([]),
      association: new FormControl(null),
      alert: new FormGroup({
        type: new FormControl(AlertType.In_App_Notification),
        reminderFrequency: new FormControl(ReminderFrequency.one_Week_before)
      })
    }, { validators: this.dateRangeValidator });
  }

  ngOnInit(): void {
    this.createEventForm.get('alert.type')?.enable({ emitEvent: false });
    this.getAlertConfigCount();

    this.subscription.add(
      this.createEventForm.get('eventType')?.valueChanges.subscribe(type => {
        const associationControl = this.createEventForm.get('association');
        if (type === InvestmentActivity.Others) {
          associationControl?.setValidators(Validators.required);
        } else {
          associationControl?.clearValidators();
          associationControl?.setValue(null, { emitEvent: false });
        }
        associationControl?.updateValueAndValidity({ emitEvent: false });
      })
    );

    if (!this.portfolioCompanyOptions.length) {
      this.subscription.add(
        this.portfolioReviewService.getAll().subscribe(companies => {
          this.portfolioCompanyOptions = companies
            .filter(c => c.stage === CompanyStage.Active)
            .map(c => ({
              label: `${c.name} · ${c.mnemonic}`,
              value: c.mnemonic
            }));
          if (this.selectedEvent?.portfolioCompanies?.length) {
            this.createEventForm.get('portfolioCompanies')?.setValue(this.selectedEvent.portfolioCompanies, { emitEvent: false });
          }
        })
      );
    }
  }

  getAlertConfigCount() {
    this.subscription.add(this.quarterService.getQuartersOfYear().subscribe(quarters => {
      this.quartersOfYear = quarters;

      if (!this.selectedEvent?.quarter) {
        const qtr = this.notificationService.getCurrentQuarter();
        this.createEventForm.patchValue({ quarter: qtr });
      }
    }));
  }

  ngAfterViewInit(): void {
    this.subscription.add(this.createEventForm.get('description')?.valueChanges.subscribe(() => {
      setTimeout(() => this.descriptionInput?.resize(), 0);
    }));

    setTimeout(() => {
      this.descriptionInput?.resize();
    }, 0);
  }

  ngOnChanges(): void {
    const isEdit = this.selectedEvent && Object.keys(this.selectedEvent).length > 0;

    if (isEdit) {
      const utcStartDate = new Date(this.selectedEvent.startDate);
      const utcEndDate = new Date(this.selectedEvent.endDate || this.getMaxDate());

      this.minDateFromDb = new Date(utcStartDate);
      this.minDateFromDb.setHours(0, 0, 0, 0);

      this.createEventForm.patchValue({
        title: this.selectedEvent.title,
        eventType: this.selectedEvent.eventType,
        startDate: utcStartDate,
        endDate: utcEndDate,
        description: this.selectedEvent.description,
        createdBy: this.selectedEvent.createdBy,
        isPublished: this.selectedEvent.isPublished,
        quarter: this.selectedEvent.quarter,
        isAdmin: this.selectedEvent.isAdmin,
        alert: {
          type: this.selectedEvent.alert?.type || AlertType.None,
          reminderFrequency: this.selectedEvent.alert?.reminderFrequency || null
        },
        teamAssignment: this.selectedEvent.teamAssignment || [],
        portfolioCompanies: this.selectedEvent.portfolioCompanies || [],
        association: this.selectedEvent.association || null
      });
    } else {
      this.minDateFromDb = new Date(this.minDate);
    }

  }

  dateRangeValidator(group: FormGroup): { [key: string]: boolean } | null {
    const start = group.get('startDate');
    const end = group.get('endDate');

    if (!start || !end) return null;

    const startValue = start.value;
    const endValue = end.value;

    // Prepare existing errors to preserve others
    const endErrors = end.errors ?? {};

    // Clear previous custom error if present
    if (endErrors['dateRangeInvalid']) {
      delete endErrors['dateRangeInvalid'];
    }

    if (startValue && endValue && startValue > endValue) {
      end.setErrors({ ...endErrors, dateRangeInvalid: true });
      return { dateRangeInvalid: true };
    }

    // Set other errors back or clear
    if (Object.keys(endErrors).length > 0) {
      end.setErrors(endErrors);
    } else {
      end.setErrors(null);
    }

    return null;
  }

  onDateChanged(type: 'start' | 'end'): void {
    const startControl = this.createEventForm.get('startDate');
    const endControl = this.createEventForm.get('endDate');

    startControl?.markAsTouched();
    endControl?.markAsTouched();

    const startDate = startControl?.value;
    const endDate = endControl?.value;

    if (type === 'start' && startDate && endDate && startDate > endDate) {
      endControl?.setValue(null);
    }

  }

  onSubmit() {
    this.createEventForm.markAllAsTouched();
    if (this.createEventForm.invalid) return;

    let { title, eventType, startDate, endDate, description, alert, teamAssignment, createdBy, isPublished, quarter, isAdmin, lastUpdatedBy, portfolioCompanies, association } = this.createEventForm.getRawValue();
    if (alert.type === AlertType.None) {
      alert = null;
    } else if (!alert.reminderFrequency) {
      alert.reminderFrequency = ReminderFrequency.one_Week_before;
    }

    const isAllTeamsSelected = teamAssignment?.length === this.teamOptions?.length;

    const data: ICalendarItem = {
      title,
      eventType,
      startDate,
      endDate,
      description,
      teamAssignment,
      alert,
      createdBy,
      isPublished,
      quarter,
      isAdmin,
      lastUpdatedBy,
      isAllTeamsSelected,
      _id: this.selectedEvent?._id,
      portfolioCompanies: portfolioCompanies?.length ? portfolioCompanies : [],
      association: eventType === InvestmentActivity.Others ? association : null
    };

    if (this.hasSelectedEvent) {
      const _updateSub = this.notificationService.updateEvent(data).subscribe({
        next: (res) => {
          this.isSubmitted.emit(res);
          this.newEvent.emit({ ...this.selectedEvent, ...data });
        },
        error: () => {}
      });
      _updateSub.add(() => {
        this.emitClose();
      });
      this.subscription.add(_updateSub);
    } else {
      // Get browser timezone
      const timeZone = this.timeZoneService.getBrowserTimeZone();
      const payload = data.quarter ? { qtr: data.quarter, timezone: timeZone } : undefined;
      const addTask: IAlertEvent = Utils.normalizeAlertEvent(data);
      this.subscription.add(this.notificationService.addNewTask(addTask, payload).subscribe({
        next: (res) => {
          this.isSubmitted.emit(res);
        },
        error: () => {}
      }));
    }
  }

  onDelete(): void {
    this.confirmationService.confirm({
      message: 'This will delete the selected event. Are you sure that you want to proceed?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.subscription.add(this.notificationService.delete(this.selectedEvent).subscribe({
          next: (res) => {
            this.isSubmitted.emit(res);
            this.emitClose();
            this.createEventForm.reset();
          },
          error: () => {}
        }));
      },
      reject: () => {}
    });
  }

  unPublishEvent(ev: ICalendarItem) {
    const updatedEvent = {
      ...ev,
      isPublished: false
    };

    this.subscription.add(this.notificationService.updateEvent(updatedEvent).subscribe({
      next: (res) => {
        this.qToaster.showToast({
          message: "Event unpublished successfully",
          status: "success"
        });

        this.newEvent.emit(updatedEvent);
        this.isSubmitted.emit(res);
        this.createEventForm.reset();
        this.emitClose();
      },
      error: () => {}
    }));
  }

  emitClose(): void {
    this._programmaticClose = true;
    this._visible = false;
    this.visibleChange.emit(false);
    this.hide.emit();
  }

  onDialogHide(): void {
    if (!this._programmaticClose) {
      this.visibleChange.emit(false);
      this.hide.emit();
    }
    this._programmaticClose = false;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  get hasSelectedEvent(): boolean {
    return this.selectedEvent && Object.keys(this.selectedEvent).length !== 0;
  }

  get excludedPortfolioCompanies(): { label: string; value: string }[] {
    const selected: string[] = this.createEventForm.get('portfolioCompanies')?.value || [];
    return this.portfolioCompanyOptions.filter(opt => !selected.includes(opt.value));
  }

  get selectedPcCount(): number {
    return (this.createEventForm.get('portfolioCompanies')?.value as string[] || []).length;
  }

  get selectedPortfolioCompanyNames(): string {
    if (!this.createEventForm) return '—';
    const values: string[] = this.createEventForm.get('portfolioCompanies')?.value || [];
    if (!values.length) return '—';
    return values.map(v => this.portfolioCompanyOptions.find(o => o.value === v)?.label || v).join(', ');
  }

  getMaxDate(): Date {
    const now = new Date();
    const currentMonth = now.getMonth(); // 0 = Jan, 11 = Dec
    const quarterEndMonth = Math.floor(currentMonth / 3) * 3 + 2; // last month of the quarter
    return new Date(now.getFullYear(), quarterEndMonth + 1, 0); // 0th day = last day of previous month
  }
}
